"use client";

import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Shirt,
  Camera,
  X,
  Check,
  Loader2,
  AlertCircle,
  Tag,
} from "lucide-react";

interface OrderItem {
  id: string;
  garmentType: string;
  qty: number;
  conditionNotes?: string | null;
  tagCode?: string | null;
  intakeConfirmedAt?: string | null;
  status: string;
}

interface IntakeItem {
  orderItemId: string;
  tagCode: string;
  intakePhotos: string[];
  conditionNotes: string;
}

interface PickupIntakeFormProps {
  items: OrderItem[];
  onConfirm: (items: IntakeItem[]) => void;
  isSubmitting: boolean;
}

export function PickupIntakeForm({
  items,
  onConfirm,
  isSubmitting,
}: PickupIntakeFormProps) {
  const [intakeData, setIntakeData] = useState<Record<string, IntakeItem>>(() => {
    const initial: Record<string, IntakeItem> = {};
    items.forEach((item) => {
      initial[item.id] = {
        orderItemId: item.id,
        tagCode: item.tagCode || "",
        intakePhotos: [],
        conditionNotes: item.conditionNotes || "",
      };
    });
    return initial;
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const updateItem = (itemId: string, field: keyof IntakeItem, value: string | string[]) => {
    setIntakeData((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value },
    }));
    setErrors((prev) => ({ ...prev, [itemId]: "" }));
  };

  const handlePhotoUpload = async (itemId: string, files: FileList | null) => {
    if (!files) return;

    const currentPhotos = intakeData[itemId]?.intakePhotos || [];
    if (currentPhotos.length + files.length > 6) {
      setErrors((prev) => ({
        ...prev,
        [itemId]: "Maximum 6 photos allowed per item",
      }));
      return;
    }

    const newPhotos: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith("image/")) continue;

      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve) => {
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsDataURL(file);
      });

      newPhotos.push(dataUrl);
    }

    updateItem(itemId, "intakePhotos", [...currentPhotos, ...newPhotos]);
  };

  const removePhoto = (itemId: string, index: number) => {
    const currentPhotos = intakeData[itemId]?.intakePhotos || [];
    const newPhotos = currentPhotos.filter((_, i) => i !== index);
    updateItem(itemId, "intakePhotos", newPhotos);
  };

  const validateAndSubmit = () => {
    const newErrors: Record<string, string> = {};
    const tagCodes: string[] = [];

    items.forEach((item) => {
      const data = intakeData[item.id];
      if (!data?.tagCode || data.tagCode.length < 3) {
        newErrors[item.id] = "Tag code must be at least 3 characters";
      } else if (tagCodes.includes(data.tagCode)) {
        newErrors[item.id] = "Duplicate tag code";
      } else {
        tagCodes.push(data.tagCode);
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const intakeItems = Object.values(intakeData);
    onConfirm(intakeItems);
  };

  const allTagsFilled = items.every(
    (item) => intakeData[item.id]?.tagCode?.length >= 3
  );

  const pendingItems = items.filter((item) => item.status === "ADDED");
  const confirmedItems = items.filter((item) => item.status !== "ADDED");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Shirt className="h-4 w-4" />
          Pickup Intake
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {confirmedItems.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">
              Already Confirmed
            </p>
            {confirmedItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200"
              >
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium">{item.garmentType}</p>
                    <p className="text-sm text-muted-foreground">
                      Tag: {item.tagCode}
                    </p>
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-700">Confirmed</Badge>
              </div>
            ))}
          </div>
        )}

        {pendingItems.length > 0 && (
          <>
            {confirmedItems.length > 0 && <Separator />}

            <div className="space-y-4">
              <p className="text-sm font-medium text-muted-foreground">
                Pending Items ({pendingItems.length})
              </p>

              {pendingItems.map((item, index) => (
                <Card key={item.id} className="border-dashed">
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Shirt className="h-5 w-5 text-primary" />
                        <span className="font-medium">{item.garmentType}</span>
                        <Badge variant="outline">x{item.qty}</Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Item {index + 1}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor={`tag-${item.id}`}
                        className="flex items-center gap-2"
                      >
                        <Tag className="h-4 w-4" />
                        Tag Code *
                      </Label>
                      <Input
                        id={`tag-${item.id}`}
                        placeholder="Enter unique tag code (e.g., A001)"
                        value={intakeData[item.id]?.tagCode || ""}
                        onChange={(e) =>
                          updateItem(item.id, "tagCode", e.target.value.toUpperCase())
                        }
                        className={errors[item.id] ? "border-red-500" : ""}
                      />
                      {errors[item.id] && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors[item.id]}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Camera className="h-4 w-4" />
                        Photos (optional, max 6)
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {(intakeData[item.id]?.intakePhotos || []).map(
                          (photo, photoIndex) => (
                            <div
                              key={photoIndex}
                              className="relative w-20 h-20 rounded-lg overflow-hidden border"
                            >
                              <img
                                src={photo}
                                alt={`Photo ${photoIndex + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <button
                                onClick={() => removePhoto(item.id, photoIndex)}
                                className="absolute top-1 right-1 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          )
                        )}
                        {(intakeData[item.id]?.intakePhotos?.length || 0) < 6 && (
                          <button
                            onClick={() => fileInputRefs.current[item.id]?.click()}
                            className="w-20 h-20 rounded-lg border-2 border-dashed flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                          >
                            <Camera className="h-6 w-6" />
                          </button>
                        )}
                        <input
                          ref={(el) => (fileInputRefs.current[item.id] = el)}
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) =>
                            handlePhotoUpload(item.id, e.target.files)
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`notes-${item.id}`}>
                        Condition Notes (optional)
                      </Label>
                      <Textarea
                        id={`notes-${item.id}`}
                        placeholder="Note any damage, stains, or special observations..."
                        value={intakeData[item.id]?.conditionNotes || ""}
                        onChange={(e) =>
                          updateItem(item.id, "conditionNotes", e.target.value)
                        }
                        rows={2}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Button
              onClick={validateAndSubmit}
              disabled={!allTagsFilled || isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Confirming Intake...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Confirm Intake ({pendingItems.length} items)
                </>
              )}
            </Button>
          </>
        )}

        {pendingItems.length === 0 && confirmedItems.length > 0 && (
          <div className="text-center py-4 text-green-600">
            <Check className="h-8 w-8 mx-auto mb-2" />
            <p className="font-medium">All items confirmed!</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
